import { Card, CardBody, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Concentration } from '../../../common/Types';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '../../../components';
import Skeleton from 'react-loading-skeleton';
import { professionService } from '../../../services/ProfessionAdministration';
import { toast } from 'react-toastify';

interface ConcentrationsCardProps {
    professionId: number;
}

const ConcentrationsCard = (props: ConcentrationsCardProps) => {
    const { professionId } = props;

    const [concentrations, setConcentrations] = useState<Concentration[]>();
    const [reload, setReload] = useState<boolean>(true);

    const [concentrationEdit, setConcentrationEdit] = useState<Concentration>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const toggleModal = () => setShowModal(!showModal);

    const [concentrationToDelete, setConcentrationToDelete] = useState<Concentration>();
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal);

    const activeConcentrations = useMemo(() => {
        return concentrations?.filter((c) => !c.isDeleted).sort((a, b) => a.title.localeCompare(b.title)) || [];
    }, [concentrations]);

    const deletedConcentrations = useMemo(() => {
        return concentrations?.filter((c) => c.isDeleted).sort((a, b) => a.title.localeCompare(b.title)) || [];
    }, [concentrations]);

    useEffect(() => {
        const getConcentrations = () => {
            if (professionId && reload) {
                setReload(false);
                professionService
                    .getProfessionConcentrations([professionId])
                    .then((result) => {
                        setConcentrations([...result]);
                    })
                    .catch(() => {
                        toast.error('Error loading concentrations');
                    });
            }
        };

        getConcentrations();
    }, [professionId, setConcentrations, reload]);

    const startNewConcentration = () => {
        setConcentrationEdit({
            concentrationId: 0,
            professionId: professionId,
            title: '',
            isDeleted: false,
        });
        toggleModal();
    };

    const startEditConcentration = (concentration: Concentration) => {
        setConcentrationEdit({
            ...concentration,
        });
        toggleModal();
    };

    const saveConcentration = () => {
        if (concentrationEdit && concentrationEdit.title) {
            const toastId = toast.info('Saving concentration...');

            professionService
                .saveConcentration(concentrationEdit)
                .then(() => {
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Concentration saved successfully',
                    });

                    setReload(true);
                    toggleModal();
                })
                .catch(() => {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Error saving concentration',
                    });
                });
        }

        if (concentrationEdit && !concentrationEdit.title) {
            toast.error('Concentration Title is required');
        }
    };

    const confirmDeleteConcentration = (concentration: Concentration) => {
        setConcentrationToDelete(concentration);
        setShowDeleteModal(true);
    };

    const deleteConcentration = () => {
        if (!concentrationToDelete) return;

        const toastId = toast.info('Deleting concentration...');

        professionService
            .saveConcentration({ ...concentrationToDelete, isDeleted: true })
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Concentration deleted successfully',
                });
                setReload(true);
                setShowDeleteModal(false);
                setConcentrationToDelete(undefined);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error deleting concentration',
                });
            });
    };

    const restoreConcentration = (concentration: Concentration) => {
        const toastId = toast.info('Restoring concentration...');

        professionService
            .saveConcentration({ ...concentration, isDeleted: false })
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Concentration restored successfully',
                });
                setReload(true);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error restoring concentration',
                });
            });
    };

    return (
        <>
            <Card>
                <CardBody className={'d-flex flex-column'}>
                    <h4 className={'card-title d-flex align-items-center justify-content-between'}>
                        <span>{`Concentrations`}</span>
                        <div>
                            <Button
                                type={'button'}
                                color={'primary'}
                                className={'btn-icon'}
                                onClick={() => startNewConcentration()}
                            >
                                <i className={`mdi mdi-plus`} />
                            </Button>
                        </div>
                    </h4>
                    {!concentrations && <Skeleton count={5} />}
                    {concentrations && activeConcentrations.length <= 0 && <Label>{`No Concentrations`}</Label>}
                    {activeConcentrations.length > 0 &&
                        activeConcentrations.map((t) => (
                            <div
                                key={t.concentrationId}
                                className={'d-flex justify-content-between align-items-center mb-2'}
                            >
                                <span>{t.title}</span>
                                <div>
                                    <Button
                                        type={'button'}
                                        color={'info'}
                                        className={'btn-icon mr-1'}
                                        onClick={() => startEditConcentration(t)}
                                    >
                                        <i className={'mdi mdi-pencil'} />
                                    </Button>
                                    <Button
                                        type={'button'}
                                        color={'danger'}
                                        className={'btn-icon'}
                                        onClick={() => confirmDeleteConcentration(t)}
                                    >
                                        <i className={'mdi mdi-trash-can-outline'} />
                                    </Button>
                                </div>
                            </div>
                        ))}

                    {deletedConcentrations.length > 0 && (
                        <>
                            <hr />
                            <h5 className={'text-muted'}>{`Deleted Concentrations`}</h5>
                            {deletedConcentrations.map((t) => (
                                <div
                                    key={t.concentrationId}
                                    className={'d-flex justify-content-between align-items-center mb-2 text-muted'}
                                >
                                    <span>{t.title}</span>
                                    <div>
                                        <Button
                                            type={'button'}
                                            color={'secondary'}
                                            className={'btn-icon'}
                                            onClick={() => restoreConcentration(t)}
                                            title={'Restore'}
                                        >
                                            <i className={'mdi mdi-restore'} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </CardBody>
            </Card>

            {concentrationEdit && (
                <Modal isOpen={showModal} toggle={toggleModal} centered={true}>
                    <ModalHeader>
                        {`${concentrationEdit.concentrationId > 0 ? `Edit ` : `New `} Concentration`}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{`Concentration Title`}</Label>
                            <Input
                                type={'text'}
                                value={concentrationEdit.title}
                                onChange={(e) => setConcentrationEdit({ ...concentrationEdit, title: e.target.value })}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color={'primary'}
                            className={'mr-2'}
                            type={'button'}
                            onClick={() => saveConcentration()}
                        >{`Submit`}</Button>
                        <Button color={'link'} onClick={() => toggleModal()} type={'button'}>{`Cancel`}</Button>
                    </ModalFooter>
                </Modal>
            )}

            <Modal isOpen={showDeleteModal} toggle={toggleDeleteModal} centered={true}>
                <ModalHeader>{`Delete Concentration`}</ModalHeader>
                <ModalBody>
                    {`Are you sure you want to delete the concentration "${concentrationToDelete?.title}"?`}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color={'danger'}
                        className={'mr-2'}
                        type={'button'}
                        onClick={() => deleteConcentration()}
                    >{`Delete`}</Button>
                    <Button color={'link'} onClick={() => toggleDeleteModal()} type={'button'}>{`Cancel`}</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ConcentrationsCard;
